/**
 * CommandPalette — Global search overlay triggered by Cmd+K.
 * Searches station codes/names, recent searches, favorites.
 * Requirements: 7.1, 7.4
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store/appStore';
import './CommandPalette.css';

const LS_RECENT = 'stationExplorer_recentSearches';
const LS_FAVORITES = 'stationExplorer_favorites';
const MAX_RESULTS = 12;

interface PaletteResult {
  id: string;
  label: string;
  sublabel?: string;
  type: 'station' | 'recent' | 'favorite';
  path: string;
}

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PaletteResult[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { allStations } = useAppStore();

  // Global Cmd+K / Ctrl+K listener
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Focus input when opening
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setActiveIndex(0);
      // ponytail: small delay for animation frame to render input
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Search logic
  const search = useCallback((q: string) => {
    if (!q.trim()) {
      // Show recent + favorites when no query
      const items: PaletteResult[] = [];
      try {
        const favs: string[] = JSON.parse(localStorage.getItem(LS_FAVORITES) || '[]');
        favs.slice(0, 5).forEach(code => {
          items.push({ id: `fav-${code}`, label: code, sublabel: 'Favorite', type: 'favorite', path: `/observations/${code}` });
        });
      } catch { /* ignore */ }
      try {
        const recents = JSON.parse(localStorage.getItem(LS_RECENT) || '[]');
        recents.slice(0, 5).forEach((r: { query: string }) => {
          items.push({ id: `recent-${r.query}`, label: r.query, sublabel: 'Recent', type: 'recent', path: '/search/station' });
        });
      } catch { /* ignore */ }
      setResults(items);
      return;
    }

    const upper = q.trim().toUpperCase();
    const items: PaletteResult[] = [];

    // ponytail: O(n) scan over allStations. Ceiling: 110k stations × keystroke.
    // Upgrade: pre-built trie or Web Worker search.
    for (const station of allStations) {
      if (items.length >= MAX_RESULTS) break;
      const code = station.stationCode?.toUpperCase() || '';
      const name = (station.tags?.name || '').toUpperCase();
      if (code.includes(upper) || name.includes(upper)) {
        items.push({
          id: station.stationCode,
          label: station.stationCode,
          sublabel: station.tags?.name || undefined,
          type: 'station',
          path: `/observations/${station.stationCode}`,
        });
      }
    }

    setResults(items);
    setActiveIndex(0);
  }, [allStations]);

  useEffect(() => { search(query); }, [query, search]);

  const select = (result: PaletteResult) => {
    setOpen(false);
    if (result.type === 'recent') {
      navigate(result.path);
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('quickSearch', { detail: { code: result.label } }));
      }, 100);
    } else {
      navigate(result.path);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[activeIndex]) {
      e.preventDefault();
      select(results[activeIndex]);
    }
  };

  if (!open) return null;

  return (
    <div className="cmd-palette-overlay" role="presentation" onClick={() => setOpen(false)}>
      <div
        className="cmd-palette"
        role="dialog"
        aria-modal="true"
        aria-label="Search stations"
        onClick={e => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="cmd-palette-input-row">
          <svg className="cmd-palette-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" /><path d="M20 20L16 16" />
          </svg>
          <input
            ref={inputRef}
            className="cmd-palette-input"
            type="text"
            placeholder="Search stations by code or name..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            aria-label="Search"
            aria-activedescendant={results[activeIndex]?.id}
          />
          <kbd className="cmd-palette-esc">Esc</kbd>
        </div>

        {results.length > 0 && (
          <ul className="cmd-palette-results" role="listbox">
            {results.map((r, i) => (
              <li
                key={r.id}
                id={r.id}
                role="option"
                aria-selected={i === activeIndex}
                className={`cmd-palette-item ${i === activeIndex ? 'active' : ''}`}
                onClick={() => select(r)}
                onMouseEnter={() => setActiveIndex(i)}
              >
                <span className="cmd-palette-item-label">{r.label}</span>
                {r.sublabel && <span className="cmd-palette-item-sub">{r.sublabel}</span>}
                <span className={`cmd-palette-item-badge cmd-palette-item-badge--${r.type}`}>
                  {r.type === 'favorite' ? '★' : r.type === 'recent' ? '↻' : ''}
                </span>
              </li>
            ))}
          </ul>
        )}

        {query && results.length === 0 && (
          <div className="cmd-palette-empty">No stations match "{query}"</div>
        )}

        <div className="cmd-palette-footer">
          <span>↑↓ Navigate</span>
          <span>↵ Select</span>
          <span>? Shortcuts</span>
        </div>
      </div>
    </div>
  );
};
